import { useCallback } from 'react';
import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { Chip, IconButton, makeStyles } from '@material-ui/core';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import { HeaderIconLinkRow, Link } from '@backstage/core-components';
import { AboutContent, AboutField } from '@backstage/plugin-catalog';
import {
  EntityInfoCard,
  catalogApiRef,
  getEntitySourceLocation,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { useEntityPermission } from '@backstage/plugin-catalog-react/alpha';
import { catalogEntityRefreshPermission } from '@backstage/plugin-catalog-common/alpha';
import {
  alertApiRef,
  errorApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';

const useStyles = makeStyles(theme => ({
  // Matches the stock About card's icon-link row: a horizontal rule above and
  // below the source/docs links, separating them from the field grid.
  linkContainer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
  // The PRM Codes field sits below the standard field grid, inside the same
  // card; a little top spacing keeps it clear of the last grid row.
  prmField: {
    marginTop: theme.spacing(1),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
}));

const copyToClipboard = (code: string) => {
  // Copying is a convenience: guard for browsers without the Clipboard API and
  // swallow rejection (e.g. permission denied) rather than throw an unhandled
  // promise rejection.
  navigator.clipboard?.writeText(code)?.catch(() => {});
};

/**
 * The System overview's About card. A faithful composition of the stock
 * `EntityAboutCard` (via the public `EntityInfoCard` + `AboutContent`) that
 * appends the org-specific **PRM Codes** as a field inside the card, replacing
 * the former standalone `PrmCodesCard` (feature 0015). Header actions (refresh,
 * edit) and the view-source link mirror the stock card so nothing is lost.
 */
export const SystemAboutCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );

  const entityMetadataEditUrl =
    entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  // Refresh is only meaningful for entities ingested from a url/file location.
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: 'Refresh scheduled',
        severity: 'info',
        display: 'transient',
      });
    } catch (e) {
      errorApi.post(e as Error);
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  const sourceLocation = getEntitySourceLocation(entity, scmIntegrationsApi);

  // The SDK's SystemEntity type has no prmCodes field, so a narrow local cast
  // is used to read the org-specific spec.prmCodes. Validate the shape at
  // runtime (catalog YAML is untyped) so a malformed value degrades to the
  // empty state rather than throwing — same guard the aws-accounts provider uses.
  const rawCodes = (entity.spec as { prmCodes?: unknown } | undefined)?.prmCodes;
  const codes = Array.isArray(rawCodes)
    ? rawCodes.filter(
        (c): c is string => typeof c === 'string' && c.trim().length > 0,
      )
    : [];

  return (
    <EntityInfoCard
      title="About"
      headerActions={
        <>
          {allowRefresh && canRefresh && (
            <IconButton
              aria-label="Refresh"
              title="Schedule entity refresh"
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )}
          <IconButton
            component={Link}
            aria-label="Edit"
            disabled={!entityMetadataEditUrl}
            title="Edit metadata"
            to={entityMetadataEditUrl ?? '#'}
          >
            <EditIcon />
          </IconButton>
        </>
      }
    >
      <div className={classes.linkContainer}>
        <HeaderIconLinkRow
          links={[
            {
              label: 'View Source',
              disabled: !sourceLocation,
              icon: (
                <ScmIntegrationIcon type={sourceLocation?.integrationType} />
              ),
              href: sourceLocation?.locationTargetUrl,
            },
          ]}
        />
      </div>

      <AboutContent entity={entity} />

      {codes.length > 0 && (
        <div className={classes.prmField}>
          <AboutField label="PRM Codes">
            <div className={classes.chips} data-testid="prm-codes-field">
              {codes.map(code => (
                <Chip
                  key={code}
                  label={code}
                  clickable
                  onClick={() => copyToClipboard(code)}
                />
              ))}
            </div>
          </AboutField>
        </div>
      )}
    </EntityInfoCard>
  );
};
